// @flow

// skipping story doc and test for this component as a hot fix
/* eslint-disable ta-code-conventions/has-matching-test */
/* eslint-disable ta-code-conventions/has-matching-storydoc */

import * as React from 'react';

import { RouteConsumer, type Route, type NavigateFunc, Link } from '@ta/common.routing';
import { type Props as LinkProps } from '@ta/common.routing/Link';
import { Closer, Opener } from '@ta/overlays.managers';

import Modal, { type Props as ModalProps } from './Modal';

/** Note: This component is deprecated.
 *
 * Use a combination of useRouteState and ModalView if you want a convenient interface to maintain state in routes */

/** A routed modal is a modal with special behavior in respect to routes.
 *
 *
 * 1. The modal still uses standard Opener/Closers to interact.
 * 2. When the modal is mounted, the route determines whether the modal should be initially opened.
 * 3. When the modal is opened, the route is updated with the corresponding parameter and name
 * 4. When the modal is closed, the route is updated by removing the parameter and name.
 * 5. When the modal is unmounted, the route is updated by removing the parameter and name.
 * 6. The name of the modal will be the same as the name as the route parameter name.
 * */

type Props = {|
  /** Globally unique name for this modal. You'll then be able to open the modal by name */
  name: string,
  /** Optional handler for modal dismiss */
  onClose?: () => void,
  /** Inner contents of the Modal. */
  children: React.Node,
  /** Provided by Opener */
  open: () => void,
  /** Provided by Closer */
  close: () => void,
  /** Provided by Opener */
  isOpen: boolean,
  /** provided by RouteConsumer */
  route: Route,
  /** provided by RouteConsumer */
  navigate: NavigateFunc,
  /** Any additional props for Modal */
  ...ModalProps,
|};

/* istanbul ignore next */
/**
 * @deprecated  Use @ta/modal.styleguide instead
 */
class RoutedModal extends React.Component<Props> {
  componentDidMount() {
    const { modal: name } = this.props.route;
    if (name === this.props.name && !this.props.isOpen) {
      this.props.open();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.updateRoute();
    } else if (prevProps.isOpen && !this.props.isOpen) {
      this.resetRoute(true);
    } else {
      const { modal: name } = this.props.route;
      // Handle situation so Back button also closes modal
      if (name !== this.props.name && this.props.isOpen) {
        this.props.close();
      }
    }
  }

  componentWillUnmount() {
    // don't close the global modal if you aren't open.
    if (this.props.isOpen) {
      this.props.close();
    }
  }

  resetRoute = (reset: boolean) => {
    const { modal: name, ...route } = this.props.route;
    if (name === this.props.name) {
      this.props.navigate(route, { reset });
    }
  };

  updateRoute = () => {
    const { modal: name } = this.props.route;
    if (name !== this.props.name) {
      this.props.navigate({ ...this.props.route, modal: this.props.name });
    }
  };

  render() {
    const { children, route, navigate, open, isOpen, close, ...modalProps } = this.props;
    return <Modal {...modalProps}>{this.props.children}</Modal>;
  }
}

type OuterProps = {|
  name: string,
  children: React.Node,
  ...ModalProps,
|};

const ModalWithRoute = /* istanbul ignore next */ ({ children, ...props }: OuterProps) => (
  <Opener name={props.name}>
    {(open, isOpen) => (
      <Closer>
        {close => (
          <RouteConsumer>
            {({ route, navigate }) => (
              <RoutedModal
                {...props}
                route={route}
                navigate={navigate}
                open={open}
                isOpen={isOpen}
                close={close}
              >
                {children}
              </RoutedModal>
            )}
          </RouteConsumer>
        )}
      </Closer>
    )}
  </Opener>
);

type UsedProps = {|
  target: any,
  to: any,
|};

type OuterLinkProps = $Diff<LinkProps, UsedProps>;

type ModalLinkProps = {|
  /** Globally unique name for this modal. You'll then be able to open the modal by name */
  name: string,
  /** Username associated with route */
  username?: string,
  /** Same props as Link; cannot use any in type UsedProps */
  ...OuterLinkProps,
|};

/** A special link which will open a routed Modal. Use a DeprecatedModalLink as opposed to a traditional Opener if you want to have a url on hover.
 * */
export const DeprecatedModalLink = /* istanbul ignore next */ ({
  name,
  username,
  onClick,
  ...linkProps
}: ModalLinkProps) => (
  <Opener name={name}>
    {open => (
      <RouteConsumer>
        {({ route }) => (
          <Link
            to={{ ...route, modal: name, username }}
            onClick={e => {
              open();
              if (onClick) {
                onClick(e);
              }
            }}
            {...linkProps}
          />
        )}
      </RouteConsumer>
    )}
  </Opener>
);

/* istanbul ignore next */
DeprecatedModalLink.defaultProps = {
  children: null,
  className: '',
  activeClassName: '',
  title: undefined,
};

export default ModalWithRoute;
